import { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Send, CheckCircle2, Star, ArrowRight, ArrowLeft, User, Home, Package } from 'lucide-react';
import { useLocation } from 'react-router-dom';

export default function Contact() {
  const location = useLocation();

  const addresses = {
    bern: {
      street: 'Mühledorfstrasse 1',
      city: '3018 Bern',
    },
    aargau: {
      street: 'Hintere Bahnhofstrasse 83',
      city: '5000 Aarau',
    },
    luzern: {
      street: 'Grossmatte 7',
      city: '6014 Luzern',
    },
    zug: {
      street: 'Baarerstrasse 77',
      city: '6300 Zug',
    },
    zurich: {
      street: 'Parkstrasse 4',
      city: '8304 Wallisellen',
    },
  };

  const getAddressForPage = () => {
    const path = location.pathname.toLowerCase();

    if (path.includes('bern')) {
      return addresses.bern;
    } else if (path.includes('aargau')) {
      return addresses.aargau;
    } else if (path.includes('luzern')) {
      return addresses.luzern;
    } else if (path.includes('zuerich') || path.includes('zurich') || path.includes('winterthur')) {
      return addresses.zurich;
    } else if (path.includes('stgallen') || path.includes('st-gallen')) {
      return addresses.zurich;
    } else if (path.includes('-zug-')) {
      return addresses.zug;
    } else if (path.includes('basel')) {
      return addresses.aargau;
    }

    return addresses.aargau;
  };

  const currentAddress = getAddressForPage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    startAddress: '',
    startPostal: '',
    startCity: '',
    endAddress: '',
    endPostal: '',
    endCity: '',
    apartmentSize: '',
    movingDate: '',
    services: [] as string[],
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  useEffect(() => {
    // Step tracking
  }, [currentStep]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (currentStep < totalSteps) {
      return;
    }

    try {
      const servicesText = formData.services.length > 0
        ? formData.services.join(', ')
        : '';

      const airtableData = {
        fields: {
          'Vorname': formData.firstName,
          'Nachname': formData.lastName,
          'Email': formData.email,
          'Telefon': formData.phone,
          'Start Adresse': formData.startAddress,
          'Start PLZ': formData.startPostal,
          'Start Ort': formData.startCity,
          'Ziel Adresse': formData.endAddress,
          'Ziel PLZ': formData.endPostal,
          'Ziel Ort': formData.endCity,
          'Wohnungsgrösse': formData.apartmentSize,
          'Umzugsdatum': formData.movingDate || '',
          'Services': servicesText || '',
          'Nachricht': formData.message || '',
          'Status': 'Neu'
        }
      };

      const AIRTABLE_TOKEN = 'pat43V7MsLYvXlxzb.26e6e150137f87b421e27d6b2ce1fd8c2ca45b3758757babca6300421be90acf';
      const AIRTABLE_BASE_ID = 'appylc36pz0KUmAuV';
      const AIRTABLE_TABLE_NAME = 'Anfragen Webseitenformular';
      const url = `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${encodeURIComponent(AIRTABLE_TABLE_NAME)}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AIRTABLE_TOKEN}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(airtableData),
      });

      if (!response.ok) {
        throw new Error('Anfrage konnte nicht gesendet werden');
      }

      setSubmitted(true);
      setShowConfetti(true);
      setTimeout(() => {
        window.location.href = '/danke';
      }, 1500);
    } catch (error) {
      alert('Es gab ein Problem beim Senden Ihrer Anfrage. Bitte versuchen Sie es erneut oder kontaktieren Sie uns telefonisch.');
    }
  };

  const nextStep = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (currentStep < totalSteps) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.firstName && formData.lastName && formData.email && formData.phone;
      case 2:
        return formData.startAddress && formData.startPostal && formData.startCity && formData.apartmentSize;
      case 3:
        return formData.endAddress && formData.endPostal && formData.endCity;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleServiceToggle = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const services = [
    'Ein- & Auspackservice',
    'Möbelmontage',
    'Endreinigung',
    'Zwischenlagerung',
    'Entsorgung',
    'Verpackungsmaterial',
  ];

  const reviews = [
    {
      name: 'Julia Weber',
      location: 'Zürich → München',
      text: 'Professioneller Umzug von Zürich nach München. Alles lief reibungslos und pünktlich. Absolut empfehlenswert!',
      rating: 5,
    },
    {
      name: 'Michael Schneider',
      location: 'Basel → Frankfurt',
      text: 'Das Team war super freundlich und sorgfältig. Kein einziges Teil wurde beschädigt. Vielen Dank!',
      rating: 5,
    },
    {
      name: 'Anna Müller',
      location: 'Bern → Stuttgart',
      text: 'Faire Preise, schnelle Abwicklung und sehr zuvorkommendes Personal. Würde ich jederzeit wieder buchen.',
      rating: 5,
    },
    {
      name: 'Thomas Keller',
      location: 'Luzern → Köln',
      text: 'Hervorragender Service! Die Zollabwicklung wurde komplett übernommen. Stress frei umgezogen!',
      rating: 5,
    },
    {
      name: 'Sarah Meyer',
      location: 'St. Gallen → Hamburg',
      text: 'Sehr professionell und zuverlässig. Die Kommunikation war ausgezeichnet. Top Service!',
      rating: 5,
    },
    {
      name: 'Robert Fischer',
      location: 'Winterthur → Berlin',
      text: 'Pünktlich, sauber und effizient. Das beste Umzugsunternehmen, das ich je hatte. Danke!',
      rating: 5,
    },
  ];

  const [currentReview, setCurrentReview] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % reviews.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [reviews.length]);

  return (
    <section id="kontakt" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-block bg-slate-800 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Kontakt aufnehmen
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Kostenlose Offerte erhalten
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Füllen Sie das Formular aus und erhalten Sie innerhalb von 24 Stunden eine transparente Fixpreis-Offerte.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="relative overflow-hidden bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-12 text-center">
                {showConfetti && (
                  <div className="absolute inset-0 pointer-events-none">
                    {[...Array(50)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute animate-fall"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: '-10px',
                          animationDelay: `${Math.random() * 3}s`,
                          animationDuration: `${2 + Math.random() * 3}s`,
                        }}
                      >
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0'][Math.floor(Math.random() * 4)],
                            transform: `rotate(${Math.random() * 360}deg)`,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
                <div className="relative z-10">
                  <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                    <CheckCircle2 className="w-12 h-12 text-green-600" />
                  </div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-4 animate-fade-in">
                    Vielen Dank für Ihre Anfrage! 🎉
                  </h3>
                  <p className="text-slate-600 text-lg mb-6 animate-fade-in-delay">
                    Wir haben Ihre Nachricht erhalten und melden uns innerhalb von 24 Stunden bei Ihnen, um eine <strong>Besichtigung vor Ort</strong> oder eine <strong>Besichtigung via Videocall</strong> zu vereinbaren.
                  </p>
                  <div className="flex items-center justify-center space-x-2 text-green-600 font-semibold animate-fade-in-delay-2">
                    <Star className="w-5 h-5 fill-current" />
                    <span>Ihr Umzug ist in besten Händen</span>
                    <Star className="w-5 h-5 fill-current" />
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100">
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-6">
                    {[1, 2, 3, 4].map((step) => (
                      <div key={step} className="flex items-center" style={{ flex: step < 4 ? '1' : '0 0 auto' }}>
                        <div className="flex flex-col items-center">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition ${
                              step <= currentStep
                                ? 'bg-slate-800 text-white'
                                : 'bg-slate-200 text-slate-400'
                            }`}
                          >
                            {step === 1 && <User className="w-5 h-5" />}
                            {step === 2 && <MapPin className="w-5 h-5" />}
                            {step === 3 && <Home className="w-5 h-5" />}
                            {step === 4 && <Package className="w-5 h-5" />}
                          </div>
                          <div className="text-xs mt-2 text-center font-medium text-slate-600 whitespace-nowrap">
                            {step === 1 && 'Kontakt'}
                            {step === 2 && 'Start'}
                            {step === 3 && 'Ziel'}
                            {step === 4 && 'Details'}
                          </div>
                        </div>
                        {step < 4 && (
                          <div
                            className={`h-1 flex-1 mx-4 rounded transition ${
                              step < currentStep ? 'bg-slate-800' : 'bg-slate-200'
                            }`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="text-center text-sm font-semibold text-slate-800 mb-4">
                    Schritt {currentStep} von {totalSteps}
                  </div>
                </div>

                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-slate-700 font-semibold mb-2">
                          Vorname *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                          placeholder="Max"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-700 font-semibold mb-2">
                          Nachname *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                          placeholder="Mustermann"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-slate-700 font-semibold mb-2">
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                        placeholder="max@beispiel.ch"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-700 font-semibold mb-2">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                        placeholder="+41 62 724 80 05"
                      />
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-slate-900">Startadresse (Schweiz)</h3>
                      <p className="text-slate-600">Von wo aus ziehen Sie um?</p>
                    </div>
                    <div>
                      <label className="block text-slate-700 font-semibold mb-2">
                        Strasse & Hausnummer *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.startAddress}
                        onChange={(e) => setFormData({ ...formData, startAddress: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                        placeholder="Musterstrasse 123"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-slate-700 font-semibold mb-2">
                          PLZ *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.startPostal}
                          onChange={(e) => setFormData({ ...formData, startPostal: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                          placeholder="8000"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-700 font-semibold mb-2">
                          Ort *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.startCity}
                          onChange={(e) => setFormData({ ...formData, startCity: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                          placeholder="Zürich"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-slate-700 font-semibold mb-2">
                        Wohnungsgrösse *
                      </label>
                      <select
                        required
                        value={formData.apartmentSize}
                        onChange={(e) => setFormData({ ...formData, apartmentSize: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                      >
                        <option value="">Bitte wählen</option>
                        <option value="1-zimmer">1 Zimmer</option>
                        <option value="1.5-zimmer">1.5 Zimmer</option>
                        <option value="2-zimmer">2 Zimmer</option>
                        <option value="2.5-zimmer">2.5 Zimmer</option>
                        <option value="3-zimmer">3 Zimmer</option>
                        <option value="3.5-zimmer">3.5 Zimmer</option>
                        <option value="4-zimmer">4 Zimmer</option>
                        <option value="4.5-zimmer">4.5 Zimmer</option>
                        <option value="5-zimmer">5 Zimmer</option>
                        <option value="5.5-zimmer">5.5 Zimmer</option>
                        <option value="6-zimmer">6 Zimmer</option>
                        <option value="6.5-zimmer">6.5 Zimmer</option>
                        <option value="7-zimmer">7 Zimmer</option>
                        <option value="7.5-zimmer">7.5 Zimmer</option>
                        <option value="8-zimmer">8 Zimmer</option>
                        <option value="8.5-zimmer">8.5 Zimmer</option>
                        <option value="9-zimmer">9 Zimmer</option>
                        <option value="9.5-zimmer">9.5 Zimmer</option>
                        <option value="10-zimmer">10 Zimmer</option>
                      </select>
                    </div>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-slate-900">Zieladresse (Deutschland)</h3>
                      <p className="text-slate-600">Wohin ziehen Sie?</p>
                    </div>
                    <div>
                      <label className="block text-slate-700 font-semibold mb-2">
                        Strasse & Hausnummer *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.endAddress}
                        onChange={(e) => setFormData({ ...formData, endAddress: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                        placeholder="Beispielweg 456"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-slate-700 font-semibold mb-2">
                          PLZ *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.endPostal}
                          onChange={(e) => setFormData({ ...formData, endPostal: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                          placeholder="80331"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-700 font-semibold mb-2">
                          Ort *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.endCity}
                          onChange={(e) => setFormData({ ...formData, endCity: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                          placeholder="München"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 4 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-slate-900">Zusatzleistungen & Details</h3>
                      <p className="text-slate-600">Welche Services benötigen Sie?</p>
                    </div>
                    <div>
                      <label className="block text-slate-700 font-semibold mb-2">
                        Gewünschter Umzugstermin
                      </label>
                      <input
                        type="date"
                        value={formData.movingDate}
                        onChange={(e) => setFormData({ ...formData, movingDate: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-700 font-semibold mb-3">
                        Gewünschte Zusatzleistungen
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {services.map((service) => (
                          <button
                            key={service}
                            type="button"
                            onClick={() => handleServiceToggle(service)}
                            className={`px-4 py-3 rounded-lg border-2 transition text-sm font-medium ${
                              formData.services.includes(service)
                                ? 'bg-slate-800 text-white border-slate-800'
                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-800'
                            }`}
                          >
                            {service}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-slate-700 font-semibold mb-2">
                        Ihre Nachricht
                      </label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-slate-800 focus:outline-none transition resize-none"
                        placeholder="Gibt es besondere Gegenstände oder Anforderungen, die wir wissen sollten?"
                      ></textarea>
                    </div>
                  </div>
                )}

                <div className="flex gap-4 mt-8">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="flex-1 bg-white hover:bg-slate-50 text-slate-800 px-6 py-4 rounded-lg font-semibold transition border-2 border-slate-200 flex items-center justify-center space-x-2"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span>Zurück</span>
                    </button>
                  )}
                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      disabled={!isStepValid()}
                      className="flex-1 bg-slate-800 hover:bg-slate-900 text-white px-6 py-4 rounded-lg font-semibold transition shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span>Weiter</span>
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="flex-1 bg-slate-800 hover:bg-slate-900 text-white px-6 py-4 rounded-lg font-semibold transition shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                    >
                      <span>Kostenlose Offerte anfordern</span>
                      <Send className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <p className="text-slate-500 text-sm mt-4 text-center">
                  * Pflichtfelder | Wir behandeln Ihre Daten vertraulich gemäss DSG/DSGVO
                </p>
              </form>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-slate-800 text-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6">Kontaktdaten</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-white/10 rounded-lg p-3">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-300 mb-1">Telefon</div>
                    <a href="tel:0800700100" className="font-semibold text-lg hover:text-slate-200 transition">
                      0800 700 100
                    </a>
                    <div className="text-xs text-green-400 font-semibold mt-1">Gratis Hotline</div>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-white/10 rounded-lg p-3">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-300 mb-1">E-Mail</div>
                    <a href="mailto:info@umzugprofis.ch" className="font-semibold hover:text-slate-200 transition">
                      info@umzugprofis.ch
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-white/10 rounded-lg p-3">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-300 mb-1">Hauptsitz</div>
                    <div className="font-semibold">
                      {currentAddress.street}
                    </div>
                    <div className="font-semibold">
                      {currentAddress.city}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Öffnungszeiten
              </h3>
              <div className="space-y-3 text-slate-700">
                <div className="flex justify-between">
                  <span>Montag – Freitag</span>
                  <span className="font-semibold">08:00 – 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span>Samstag</span>
                  <span className="font-semibold">08:00 – 13:00</span>
                </div>
                <div className="flex justify-between">
                  <span>Sonntag</span>
                  <span className="font-semibold">Geschlossen</span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t-2 border-slate-200">
                <p className="text-sm text-slate-600">
                  Notfall-Hotline 24/7 für dringende Umzüge verfügbar
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border-2 border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Kundenbewertungen
              </h3>
              <div className="relative">
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 min-h-[180px]">
                  <div className="flex items-center mb-3">
                    {[...Array(reviews[currentReview].rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 text-sm mb-4 leading-relaxed">
                    "{reviews[currentReview].text}"
                  </p>
                  <div className="border-t border-slate-200 pt-3">
                    <div className="font-semibold text-slate-900">{reviews[currentReview].name}</div>
                    <div className="text-xs text-slate-500">{reviews[currentReview].location}</div>
                  </div>
                </div>

                <div className="flex justify-center gap-1.5 mt-4">
                  {reviews.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentReview(index)}
                      className={`transition-all rounded-full ${
                        index === currentReview
                          ? 'bg-slate-800'
                          : 'bg-slate-300 hover:bg-slate-400'
                      }`}
                      style={{ width: index === currentReview ? '20px' : '8px', height: '8px' }}
                      aria-label={`Go to review ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
